import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Sparkles } from "lucide-react";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden pt-36 pb-24 md:pt-44 md:pb-32"
    >
      {/* Floral blobs */}
      <div className="floral-blob w-[420px] h-[420px] bg-[#D88C76]/30 -top-24 -left-24" />
      <div className="floral-blob w-[380px] h-[380px] bg-[#8DA399]/40 top-40 right-[-100px]" />

      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left: content */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-2 mb-6"
            >
              <span className="eyebrow flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" />
                Accredited MHFA England Training
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-serif-display text-[3.2rem] leading-[1.02] md:text-[5.2rem] md:leading-[0.98] text-[#1E2A25] tracking-tight font-light"
            >
              Mental health <br />
              <span className="italic text-[#4A5D52]">first aid</span> for<br />
              modern workplaces.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-8 max-w-xl text-lg text-[#4A5D52] leading-relaxed"
            >
              Warm, evidence-based training that gives your team the skills, confidence and language
              to support colleagues, spot the signs early, and build a culture where wellbeing thrives.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link to="/courses" data-testid="hero-cta-explore" className="btn-primary">
                Explore courses <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" data-testid="hero-cta-enquire" className="btn-secondary">
                Enquire for your team
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="mt-14 flex items-center gap-6 text-sm text-[#4A5D52]"
            >
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-[#D88C76]" />
                <span>10+ years delivering</span>
              </div>
              <span className="w-1 h-1 rounded-full bg-[#8DA399]" />
              <div>Trusted by teams UK-wide</div>
              <span className="w-1 h-1 rounded-full bg-[#8DA399] hidden md:block" />
              <div className="hidden md:block">Royal Society for Public Health accredited</div>
            </motion.div>
          </div>

          {/* Right: image collage */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative"
            >
              <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-[0_40px_80px_-30px_rgba(30,42,37,0.35)]">
                <img
                  src="https://customer-assets-gfyr7b9c.emergentagent.net/job_mhfa-course-hub/artifacts/8h6tlegz_geralt-brainstorming-10174574.webp"
                  alt="Collaborative workshop with post-it notes"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1E2A25]/25 via-transparent to-transparent" />
              </div>

              {/* Floating stat card */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="absolute -left-4 md:-left-10 bottom-10 bg-[#FDFBF7] border border-[#8DA399]/30 rounded-2xl p-5 pr-8 max-w-[240px] shadow-lg"
              >
                <div className="font-serif-display text-4xl text-[#4A5D52]">800k+</div>
                <p className="mt-1 text-sm text-[#4A5D52] leading-snug">
                  UK adults now trained as Mental Health First Aiders.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
